<script>
export default {
  name: 'RobDetail',
  props: {
    selectedRobDetail: Object,
    detailLoading: Boolean,
    detailError: String,
    adjustmentData: Array,
    adjustmentLoading: Boolean,
    adjustmentError: String,
    confirmingDeleteAdjustmentId: [Number, String],
    summaryLoading: Boolean, // Prop for summary-specific loading
  },
  data() {
    return {
      detailViewType: 'summary',
      summaryInterval: 'daily',
    };
  },
  computed: {
    // Extracts and flattens all logs from the nested structure to display in a single table
    flattenedLogs() {
      if (!this.selectedRobDetail?.summary?.details) return [];
      const logs = [];
      this.selectedRobDetail.summary.details.forEach((day) => {
        Object.keys(day.logs).forEach((timestamp) => {
          const logEntries = day.logs[timestamp];
          Object.keys(logEntries).forEach((entryType) => {
            logEntries[entryType].forEach((entry) => {
              logs.push({
                timestamp: parseInt(timestamp, 10),
                ...entry,
                entryType,
              });
            });
          });
        });
      });
      return logs.sort((a, b) => a.timestamp - b.timestamp);
    },
    // Easy access to the rob summary data
    robSummary() {
      return this.selectedRobDetail?.summary?.rob || null;
    },
  },
  watch: {
    summaryInterval(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.$emit('summary-interval-change', newValue);
      }
    },
  },
  methods: {
    toggleDetailView(type) {
      this.detailViewType = type;
    },
    // Formatting methods
    formatNumber(value) {
      if (value === null || typeof value === 'undefined') return '';
      const numberValue = Number(value);
      if (Number.isNaN(numberValue)) return value;
      return numberValue.toFixed(1);
    },
    formatDateToLocal(dateString) {
      if (!dateString) return '';
      const date = new Date(`${dateString.replace(' ', 'T')}Z`);
      const dateOptions = { day: 'numeric', month: 'long', year: 'numeric' };
      const timeOptions = {
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
      };
      return `${date.toLocaleDateString('en-US', dateOptions)}, ${date.toLocaleTimeString('en-US', timeOptions)}`;
    },
    formatUnixTimestampToLocal(unixTimestamp) {
      if (!unixTimestamp) return '';
      const date = new Date(unixTimestamp * 1000);
      const dateOptions = { day: 'numeric', month: 'long', year: 'numeric' };
      const timeOptions = {
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
      };
      return `${date.toLocaleDateString('en-US', dateOptions)}, ${date.toLocaleTimeString('en-US', timeOptions)}`;
    },
    formatAdjustmentType(type) {
      return Number(type) === 1 ? 'Reset Value' : 'Adjust Value';
    },
    formatEntryType(type) {
      switch (type) {
        case 'fuelcons':
          return 'Fuel Consumption';
        case 'bunker':
          return 'Bunker';
        case 'adjustments':
          return 'Adjustment';
        default:
          return type;
      }
    },
  },
};
</script>

<template>
  <div>
    <div class="d-flex justify-content-between align-items-center mb-3">
      <div>
        <b-button variant="outline-secondary" size="sm" @click="$emit('back')" class="mr-2">
          <i class="ri-arrow-left-line"></i> Back
        </b-button>
        <h5 class="d-inline-block mb-0">ROB Profile Detail</h5>
      </div>
    </div>
    <div v-if="detailLoading" class="text-center">
      <p>Loading details...</p>
    </div>
    <div v-else-if="detailError" class="alert alert-danger">
      {{ detailError }}
    </div>
    <div v-else-if="selectedRobDetail">
      <!-- ROB Profile Info Card -->
      <b-card class="mb-4">
        <template #header>
          <h5 class="mb-0">ROB Profile</h5>
        </template>
        <b-row>
          <b-col md="4" class="mb-3">
            <div class="d-flex align-items-center">
              <div class="mr-3">
                <i class="ri-time-line" style="font-size: 2.2rem; color: #50b5ff;"></i>
              </div>
              <div>
                <div class="text-muted small">Start Time</div>
                <strong>{{ formatDateToLocal(selectedRobDetail.master.mrobStartTime) }}</strong>
              </div>
            </div>
          </b-col>
          <b-col md="4" class="mb-3">
            <div class="d-flex align-items-center">
              <div class="mr-3">
                <i class="ri-time-fill" style="font-size: 2.2rem; color: #50b5ff;"></i>
              </div>
              <div>
                <div class="text-muted small">End Time</div>
                <strong>{{ formatDateToLocal(selectedRobDetail.master.mrobEndTime) }}</strong>
              </div>
            </div>
          </b-col>
          <b-col md="4" class="mb-3">
            <div class="d-flex align-items-center">
              <div class="mr-3">
                <i class="ri-gas-station-line" style="font-size: 2.2rem; color: #50b5ff;"></i>
              </div>
              <div>
                <div class="text-muted small">Initial Value</div>
                <strong>{{ formatNumber(selectedRobDetail.master.mrobInitialValue) }}</strong>
              </div>
            </div>
          </b-col>
          <b-col md="4" class="mb-3">
            <div class="d-flex align-items-center">
              <div class="mr-3">
                <i class="ri-user-line" style="font-size: 2.2rem; color: #50b5ff;"></i>
              </div>
              <div>
                <div class="text-muted small">Created By</div>
                <strong>{{ selectedRobDetail.master.mrobCreatedUserId || '-' }}</strong>
              </div>
            </div>
          </b-col>
          <b-col md="4" class="mb-3">
            <div class="d-flex align-items-center">
              <div class="mr-3">
                <i class="ri-calendar-event-line" style="font-size: 2.2rem; color: #50b5ff;"></i>
              </div>
              <div>
                <div class="text-muted small">Created At</div>
                <strong>{{ formatDateToLocal(selectedRobDetail.master.mrobCreatedTime) }}</strong>
              </div>
            </div>
          </b-col>
        </b-row>
      </b-card>

      <!-- New Summary Cards -->
      <h5 class="mt-4" v-if="canRead('summary_rob')">Summary</h5>
      <div v-if="summaryLoading" class="text-center py-3">
        <b-spinner small></b-spinner> Loading summary...
      </div>
      <div v-else-if="robSummary && canRead('summary_rob')">
        <b-row>
          <!-- Start Balance Card -->
          <b-col lg="2" md="4" sm="6" class="mb-4">
            <b-card class="summary-card h-100" no-body>
              <div class="card-header d-flex align-items-center" style="background-color: #e9f5ff; color: #007bff;">
                <i class="ri-inbox-unarchive-line mr-2" style="font-size: 1.2rem;"></i>
                <span class="font-weight-bold">Start Balance</span>
              </div>
              <b-card-body class="d-flex flex-column justify-content-center text-center">
                <h4 class="mb-0 font-weight-bold">{{ formatNumber(robSummary.balances.start) }}</h4>
              </b-card-body>
            </b-card>
          </b-col>
          <!-- Last Balance Card -->
          <b-col lg="2" md="4" sm="6" class="mb-4">
            <b-card class="summary-card h-100" no-body>
              <div class="card-header d-flex align-items-center" style="background-color: #e9f5ff; color: #007bff;">
                <i class="ri-inbox-archive-line mr-2" style="font-size: 1.2rem;"></i>
                <span class="font-weight-bold">Last Balance</span>
              </div>
              <b-card-body class="d-flex flex-column justify-content-center text-center">
                <h4 class="mb-0 font-weight-bold">{{ formatNumber(robSummary.balances.last) }}</h4>
              </b-card-body>
            </b-card>
          </b-col>
          <!-- Consumption Card -->
          <b-col lg="2" md="4" sm="6" class="mb-4">
            <b-card class="summary-card h-100" no-body>
              <div class="card-header d-flex align-items-center" style="background-color: #fff4e6; color: #fd7e14;">
                <i class="ri-fire-line mr-2" style="font-size: 1.2rem;"></i>
                <span class="font-weight-bold">Consumption</span>
              </div>
              <b-card-body class="d-flex flex-column justify-content-center text-center">
                <div class="text-muted small">Total Volume</div>
                <h4 class="mb-0 font-weight-bold">{{ formatNumber(robSummary.consumption.totalVolume) }}</h4>
              </b-card-body>
            </b-card>
          </b-col>
          <!-- Total Adjustment Vol Card -->
          <b-col lg="2" md="4" sm="6" class="mb-4">
            <b-card class="summary-card h-100" no-body>
              <div class="card-header d-flex align-items-center" style="background-color: #f3e9ff; color: #6f42c1;">
                <i class="ri-tools-line mr-2" style="font-size: 1.2rem;"></i>
                <span class="font-weight-bold">Total Adj. Vol</span>
              </div>
              <b-card-body class="d-flex flex-column justify-content-center text-center">
                <h4 class="mb-0 font-weight-bold">{{ formatNumber(robSummary.adjustments.totalVolume) }}</h4>
              </b-card-body>
            </b-card>
          </b-col>
          <!-- Last Adjustment Vol Card -->
          <b-col lg="2" md="4" sm="6" class="mb-4">
            <b-card class="summary-card h-100" no-body>
              <div class="card-header d-flex align-items-center" style="background-color: #f3e9ff; color: #6f42c1;">
                <i class="ri-tools-line mr-2" style="font-size: 1.2rem;"></i>
                <span class="font-weight-bold">Last Adj. Vol</span>
              </div>
              <b-card-body class="d-flex flex-column justify-content-center text-center">
                <h4 class="mb-0 font-weight-bold">{{ formatNumber(robSummary.adjustments.lastVolume) }}</h4>
              </b-card-body>
            </b-card>
          </b-col>
          <!-- Bunker Card -->
          <b-col lg="2" md="4" sm="6" class="mb-4">
            <b-card class="summary-card h-100" no-body>
              <div class="card-header d-flex align-items-center" style="background-color: #e4f8f0; color: #28a745;">
                <i class="ri-gas-station-fill mr-2" style="font-size: 1.2rem;"></i>
                <span class="font-weight-bold">Bunker (IN)</span>
              </div>
              <b-card-body class="d-flex flex-column justify-content-center text-center">
                 <div class="text-muted small">Total Volume</div>
                 <h4 class="mb-0 font-weight-bold">{{ robSummary.bunker && robSummary.bunker.IN ? formatNumber(robSummary.bunker.IN.totalVolume) : '-' }}</h4>
              </b-card-body>
            </b-card>
          </b-col>
        </b-row>
      </div>
      <b-alert v-else-if="!summaryLoading && canRead('summary_rob')" show variant="warning">
        Summary data is not available for this ROB profile.
      </b-alert>

      <!-- View Toggler (Summary/Adjustment) -->
      <div class="d-flex justify-content-between align-items-center mb-3">
        <b-nav pills>
          <b-nav-item v-if="canRead('summary_rob')" :active="detailViewType === 'summary'"
            @click="toggleDetailView('summary')">Summary</b-nav-item>
          <b-nav-item v-if="canRead('master_rob_adjustment')" :active="detailViewType === 'adjustment'"
            @click="toggleDetailView('adjustment')">
            Adjustment
            <b-badge v-if="robSummary && robSummary.adjustments.count > 0" variant="success" class="ml-2">
              {{ robSummary.adjustments.count }} Adjusted
            </b-badge>
          </b-nav-item>
        </b-nav>
        <b-button v-if="detailViewType === 'adjustment' && canCreate('master_rob_adjustment')" variant="primary"
          @click="$emit('add-adjustment')">
          <i class="ri-add-line mr-1"></i> Add Adjustment
        </b-button>
      </div>

      <!-- Summary Detail Log -->
      <div v-if="detailViewType === 'summary' && canRead('summary_rob')">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h5 class="mb-0">Detail Log</h5>
          <b-form-group label="Interval" label-for="summary-interval-radios" class="mb-0" label-class="font-weight-bold">
            <b-form-radio-group id="summary-interval-radios" v-model="summaryInterval"
              :options="[ { text: 'Daily', value: 'daily' }, { text: 'Weekly', value: 'weekly' }, { text: 'Monthly', value: 'monthly' } ]"
              buttons button-variant="outline-primary" name="summary-interval-radios">
            </b-form-radio-group>
          </b-form-group>
        </div>

        <!-- Log Table -->
        <div v-if="summaryLoading" class="text-center py-5">
          <b-spinner label="Loading..."></b-spinner>
          <p class="mt-2 text-muted">Loading log...</p>
        </div>
        <div v-else class="table-responsive">
          <table class="table table-striped">
            <thead>
              <tr>
                <th style="width: 135px">Time</th>
                <th style="width: 135px">Type</th>
                <th>Note</th>
                <th style="width: 135px">Addition</th>
                <th style="width: 135px">Reduction</th>
                <th style="width: 135px">ROB Balance</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(log, index) in flattenedLogs" :key="index">
                <td>{{ formatUnixTimestampToLocal(log.timestamp) }}</td>
                <td class="text-capitalize">{{ formatEntryType(log.entryType) }}</td>
                <td>
                  <a href="#" v-if="log.note" @click.prevent="$emit('show-note', log.note)">
                    {{ log.note.length > 50 ? log.note.substring(0, 50) + '...' : log.note }}
                  </a>
                </td>
                <td>
                  <span v-if="log.inventoryChange >= 0" class="text-success font-weight-bold">
                    + {{ formatNumber(log.inventoryChange) }}
                  </span>
                </td>
                <td>
                  <span v-if="log.inventoryChange < 0" class="text-danger font-weight-bold">
                    - {{ formatNumber(Math.abs(log.inventoryChange)) }}
                  </span>
                </td>
                <td>{{ formatNumber(log.endInventory) }}</td>
              </tr>
              <tr v-if="flattenedLogs.length === 0">
                <td colspan="6" class="text-center">No log data available for this interval.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Adjustment Table -->
      <div v-if="detailViewType === 'adjustment' && canRead('master_rob_adjustment')">
        <h5>Adjustments</h5>
        <div v-if="adjustmentLoading">Loading...</div>
        <div v-else-if="adjustmentError">{{ adjustmentError }}</div>
        <div v-else-if="Array.isArray(adjustmentData) && adjustmentData.length > 0" class="table-responsive">
          <table class="table table-striped">
            <thead>
              <tr>
                <th>Time</th>
                <th>Type</th>
                <th>Value</th>
                <th>Note</th>
                <th>Created By</th>
                <th>Created At</th>
                <th v-if="canUpdate('master_rob_adjustment') || canDelete('master_rob_adjustment')">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="adj in adjustmentData" :key="adj.robaId">
                <td>{{ formatDateToLocal(adj.robaTime) }}</td>
                <td>{{ formatAdjustmentType(adj.robaType) }}</td>
                <td>{{ formatNumber(adj.robaValue) }}</td>
                <td>
                  <a href="#" v-if="adj.robaNote" @click.prevent="$emit('show-note', adj.robaNote)">
                    {{ adj.robaNote.length > 20 ? adj.robaNote.substring(0, 20) + '...' : adj.robaNote }}
                  </a>
                </td>
                <td>{{ adj.robaCreatedUserId }}</td>
                <td>{{ formatDateToLocal(adj.robaCreatedTime) }}</td>
                <td>
                  <button v-if="canUpdate('master_rob_adjustment')" class="btn btn-warning btn-sm mr-1"
                    @click="$emit('edit-adjustment', adj)">Edit</button>
                  <button v-if="canDelete('master_rob_adjustment')" class="btn btn-sm"
                    :class="confirmingDeleteAdjustmentId === adj.robaId ? 'btn-danger' : 'btn-outline-danger'"
                    @click="$emit('delete-adjustment', adj)" @mouseleave="$emit('update:confirmingDeleteAdjustmentId', null)">
                    {{ confirmingDeleteAdjustmentId === adj.robaId ? 'Confirm?' : 'Delete' }}
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="text-center">
          <p>No adjustment data available.</p>
        </div>
      </div>
    </div>
    <div v-else class="text-center">
      <p>No detail data available.</p>
    </div>
  </div>
</template>

<style scoped>
.table-responsive .table th,
.table-responsive .table td {
  white-space: nowrap;
}

.summary-card {
  border: 1px solid #e8ecf3;
  border-radius: .25rem;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease-in-out;
}

.summary-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
}

.summary-card .card-header {
  border-bottom: none;
  padding: 0.75rem 1rem;
}

.summary-card .card-body {
  padding: 1.5rem 1rem;
}
</style>
